import axios from 'axios';
import baseURL from './baseUrl';

/** Digits only; prepend 91 for 10-digit Indian numbers. */
export function normalizeWhatsAppNumber(raw) {
  if (!raw) return '';
  let digits = String(raw).replace(/\D/g, '');
  if (digits.length === 10) {
    digits = `91${digits}`;
  }
  return digits;
}

function truncateWhole(n) {
  const num = Number(n) || 0;
  return Math.trunc(num);
}

function formatMoney(n) {
  const value = truncateWhole(Math.abs(Number(n) || 0));
  return `₹${value.toLocaleString('en-IN')}`;
}

function formatDate(date) {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

function hasText(value) {
  if (value == null) return false;
  const text = String(value).trim();
  if (!text) return false;
  const upper = text.toUpperCase();
  return upper !== 'N/A' && upper !== '-' && upper !== 'NULL' && upper !== 'UNDEFINED';
}

function hasMeaningfulNumber(value) {
  if (value == null || value === '') return false;
  const num = Number(value);
  return Number.isFinite(num) && num !== 0;
}

function formatTypeLabel(transaction) {
  const raw = transaction.type || transaction.transactionType || transaction.advanceType || '';
  if (!hasText(raw)) return '';
  const map = {
    buy: 'Buy',
    sell: 'Sell',
    selling: 'Sell',
    adding: 'Add',
    speed: 'Speed',
    advance: 'Advance',
    given: 'Advance Given',
    taken: 'Advance Taken',
    cash: 'Cash',
    pure_weight: 'Pure Weight'
  };
  const key = String(raw).toLowerCase();
  return map[key] || String(raw).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatArticleLabel(transaction) {
  if (transaction.articleType === 'article') {
    return hasText(transaction.articleName) ? transaction.articleName : 'Article';
  }
  const map = {
    langdiWeight: 'Langdi Weight',
    pureWeight: 'Pure Weight',
    cashAmount: 'Cash'
  };
  return map[transaction.articleType] || (hasText(transaction.articleType) ? transaction.articleType : '');
}

function pushLine(lines, label, value) {
  if (!hasText(value) && typeof value !== 'number') return;
  if (typeof value === 'number' && !hasMeaningfulNumber(value)) return;
  lines.push(`${label}: ${value}`);
}

/** Build a clean, structured WhatsApp receipt message. */
export function buildTransactionWhatsAppMessage(transaction) {
  if (!transaction) return '';

  const lines = ['*Speed Touch Gold*', '────────────────'];

  if (hasText(transaction.receiptNo)) {
    pushLine(lines, 'Receipt No', transaction.receiptNo);
  }

  const dateText = formatDate(transaction.date);
  if (dateText) {
    pushLine(lines, 'Date', dateText);
  }

  if (hasText(transaction.clientName)) {
    pushLine(lines, 'Client', transaction.clientName);
  }

  const typeLabel = formatTypeLabel(transaction);
  if (typeLabel) {
    pushLine(lines, 'Type', typeLabel);
  }

  const articleLabel = formatArticleLabel(transaction);
  if (articleLabel) {
    pushLine(lines, 'Article', articleLabel);
  }

  const unit = transaction.unit || 'g';
  const detailLines = [];

  if (transaction.articleType === 'langdiWeight' && Array.isArray(transaction.amount)) {
    transaction.amount.forEach((amt, i) => {
      if (!hasMeaningfulNumber(amt) && Number(amt) !== 0) return;
      if (amt == null || amt === '') return;
      const touch = transaction.touch?.[i];
      const rate = transaction.rate?.[i];
      const parts = [`${amt} ${unit}`];
      if (hasText(touch) || hasMeaningfulNumber(touch)) {
        parts.push(`Touch ${touch}%`);
      }
      if (hasMeaningfulNumber(rate)) {
        parts.push(`Rate ${formatMoney(rate)}/10g`);
      }
      detailLines.push(`• ${parts.join(' | ')}`);
    });
  } else if (transaction.articleType === 'pureWeight' && Array.isArray(transaction.amount)) {
    transaction.amount.forEach((amt, i) => {
      if (amt == null || amt === '') return;
      const rate = transaction.rate?.[i];
      const parts = [`${amt} ${unit}`];
      if (hasMeaningfulNumber(rate)) parts.push(`Rate ${formatMoney(rate)}/10g`);
      detailLines.push(`• ${parts.join(' | ')}`);
    });
  } else if (transaction.articleType === 'article' && Array.isArray(transaction.amount)) {
    transaction.amount.forEach((amt, i) => {
      if (amt == null || amt === '') return;
      const rate = transaction.rate?.[i];
      const parts = [`${amt} PCS`];
      if (hasMeaningfulNumber(rate)) parts.push(`Rate ${formatMoney(rate)}`);
      detailLines.push(`• ${parts.join(' | ')}`);
    });
  } else if (transaction.articleType === 'cashAmount' && Array.isArray(transaction.amount)) {
    transaction.amount.forEach((amt) => {
      if (!hasMeaningfulNumber(amt)) return;
      detailLines.push(`• Cash Amount: ${formatMoney(amt)}`);
    });
  }

  if (detailLines.length) {
    lines.push('', '*Particulars*');
    lines.push(...detailLines);
  }

  lines.push('', '*Summary*');

  const total = transaction.total ?? transaction.totalAmount;
  if (hasMeaningfulNumber(total)) {
    lines.push(`Total Amount: *${formatMoney(total)}*`);
  }

  if (hasMeaningfulNumber(transaction.amountGiven)) {
    const givenLabel =
      String(transaction.type || '').toLowerCase() === 'buy'
        ? 'Amount Given'
        : 'Amount Received';
    lines.push(`${givenLabel}: ${formatMoney(transaction.amountGiven)}`);
  }

  if (hasMeaningfulNumber(transaction.pendingAmount)) {
    lines.push(`Pending Amount: ${formatMoney(transaction.pendingAmount)}`);
  }

  if (hasMeaningfulNumber(transaction.advance?.cashAmount || transaction.advanceCashAmount || transaction.advanceApplied)) {
    const adv =
      transaction.advance?.cashAmount ??
      transaction.advanceCashAmount ??
      transaction.advanceApplied;
    lines.push(`Advance Applied: ${formatMoney(adv)}`);
  }

  if (hasMeaningfulNumber(transaction.pureWeightCalculated)) {
    const pure = Number(transaction.pureWeightCalculated);
    lines.push(`Pure Weight: ${pure.toFixed(3)} ${unit}`);
  }

  // If summary only has the header with no values, remove empty Summary section
  const summaryIndex = lines.indexOf('*Summary*');
  if (summaryIndex !== -1 && summaryIndex === lines.length - 1) {
    lines.splice(summaryIndex - 1, 2); // remove blank line + Summary
  }

  lines.push('', 'Thank you!');
  return lines.join('\n');
}

export async function fetchClientWhatsappNo(clientName) {
  if (!clientName) return '';
  try {
    const response = await axios.get(`${baseURL}/api/clients`);
    const client = (response.data || []).find(
      (c) => c.name?.toLowerCase() === String(clientName).toLowerCase()
    );
    return client?.whatsappNo || '';
  } catch (err) {
    console.error('Error fetching client WhatsApp no:', err);
    return '';
  }
}

export async function saveClientWhatsappNo(clientName, whatsappNo) {
  if (!clientName || !String(clientName).trim()) return null;
  try {
    const response = await axios.put(`${baseURL}/api/clients/whatsapp`, {
      name: String(clientName).trim(),
      whatsappNo: whatsappNo != null ? String(whatsappNo).trim() : ''
    });
    return response.data;
  } catch (err) {
    console.warn('Could not save WhatsApp number:', err?.response?.data || err.message);
    return null;
  }
}

/**
 * Ask for number if missing, save it on the client ledger, open WhatsApp Web.
 * @returns {Promise<boolean>} true if WhatsApp was opened
 */
export async function sendTransactionOnWhatsApp(transaction, options = {}) {
  if (!transaction) return false;

  let clientName = String(
    transaction.clientName || options.clientName || ''
  ).trim();
  if (!clientName || clientName.toUpperCase() === 'N/A') {
    clientName = '';
  }

  let phone = normalizeWhatsAppNumber(
    options.whatsappNo || (clientName ? await fetchClientWhatsappNo(clientName) : '')
  );

  if (!phone) {
    if (!clientName) {
      const enteredName = window.prompt(
        'Client name is missing on this transaction.\nEnter client name to save WhatsApp number on client ledger:',
        ''
      );
      if (!enteredName || !enteredName.trim()) {
        window.alert('Client name is required to save WhatsApp number.');
        return false;
      }
      clientName = enteredName.trim();
    }

    const entered = window.prompt(
      `Enter WhatsApp number for "${clientName}" (10 digits or with country code):`,
      ''
    );
    if (!entered || !entered.trim()) {
      return false;
    }
    phone = normalizeWhatsAppNumber(entered);
    if (!phone || phone.length < 10) {
      window.alert('Please enter a valid WhatsApp number.');
      return false;
    }

    const saved = await saveClientWhatsappNo(clientName, phone);
    if (!saved) {
      window.alert(
        'Could not save WhatsApp number to client ledger. Please try again from Clients or Edit.'
      );
      // Still open WhatsApp so user can send
    }
  }

  const message = buildTransactionWhatsAppMessage({
    ...transaction,
    clientName: transaction.clientName || clientName
  });
  const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  window.open(url, '_blank');
  return true;
}
